import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useAuth } from "../lib/auth";
import AppHeader from "../components/AppHeader";
import type {
  CohortRow,
  LessonRow,
  ModuleRow,
  EnrollmentRow,
  LessonProgressRow,
  AssignmentSubmissionStatusRow,
} from "../lib/types";
import { fetchProgressForLessons } from "../lib/lessons";
import { fetchAssignmentStatuses } from "../lib/submissions";
import {
  computeCompletionForEnrollment,
  persistCompletion,
} from "../lib/enrollment-completion";
import type { CompletionResult } from "../lib/completion";
import { openCertificate } from "../lib/api";

// Gated student dashboard. Shows the cohort, the module/lesson structure with
// per-lesson completion ticks, and assignment status pills (not started / draft
// / submitted). Lessons link into the reader view. Renders graceful empty states
// when migrations/data aren't present yet (the live DB may not have them applied).

interface ModuleWithLessons extends ModuleRow {
  lessons: LessonRow[];
}

export default function Dashboard() {
  const { user, student } = useAuth();
  const studentId = student?.id ?? null;
  const [cohort, setCohort] = useState<CohortRow | null>(null);
  const [modules, setModules] = useState<ModuleWithLessons[]>([]);
  const [progress, setProgress] = useState<Record<string, LessonProgressRow>>(
    {},
  );
  const [assignmentStatus, setAssignmentStatus] = useState<
    Record<string, AssignmentSubmissionStatusRow>
  >({});
  const [loading, setLoading] = useState(true);
  const [dbReachable, setDbReachable] = useState(true);
  const [completion, setCompletion] = useState<CompletionResult | null>(null);
  const [enrollmentId, setEnrollmentId] = useState<string | null>(null);
  const [completionRecordId, setCompletionRecordId] = useState<string | null>(
    null,
  );
  const [certBusy, setCertBusy] = useState(false);
  const [certError, setCertError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    async function load() {
      // Pull the seed cohort. (Enrollment linking arrives with Stripe, Day 3.)
      const { data: cohorts, error: cohortErr } = await supabase
        .from("cohorts")
        .select(
          "id, name, slug, seat_cap, seats_sold, status, session1_at, session2_at, session3_at, zoom_join_url",
        )
        .order("created_at", { ascending: true })
        .limit(1);

      if (!active) return;

      if (cohortErr) {
        setDbReachable(false);
        setLoading(false);
        return;
      }

      const c = (cohorts?.[0] as CohortRow | undefined) ?? null;
      setCohort(c);

      if (c) {
        const { data: mods } = await supabase
          .from("modules")
          .select(
            "id, cohort_id, session_no, block_no, title, order, objectives",
          )
          .eq("cohort_id", c.id)
          .order("order", { ascending: true });

        const moduleList = (mods as ModuleRow[] | null) ?? [];
        const withLessons: ModuleWithLessons[] = [];
        for (const m of moduleList) {
          const { data: lessons } = await supabase
            .from("lessons")
            .select(
              "id, module_id, type, title, order, body_ref, required_for_completion",
            )
            .eq("module_id", m.id)
            .order("order", { ascending: true });
          withLessons.push({
            ...m,
            lessons: (lessons as LessonRow[] | null) ?? [],
          });
        }
        if (active) setModules(withLessons);

        // Progress + assignment status (graceful if tables/data are absent).
        const allLessons = withLessons.flatMap((m) => m.lessons);
        const allLessonIds = allLessons.map((l) => l.id);
        const assignmentLessonIds = allLessons
          .filter((l) => l.type === "workbook" || l.type === "assignment")
          .map((l) => l.id);

        if (studentId && allLessonIds.length > 0) {
          try {
            const prog = await fetchProgressForLessons(studentId, allLessonIds);
            if (active) setProgress(prog);
          } catch {
            /* lesson_progress may not exist yet — ignore */
          }

          // Resolve the student's enrollment in this cohort for assignment status.
          const { data: enr } = await supabase
            .from("enrollments")
            .select("id, student_id, cohort_id, completion_status, completed_at")
            .eq("student_id", studentId)
            .eq("cohort_id", c.id)
            .maybeSingle();
          const enrollment = (enr as EnrollmentRow | null) ?? null;
          if (enrollment) {
            if (active) setEnrollmentId(enrollment.id);
            if (assignmentLessonIds.length > 0) {
              try {
                const st = await fetchAssignmentStatuses(
                  enrollment.id,
                  assignmentLessonIds,
                );
                if (active) setAssignmentStatus(st);
              } catch {
                /* assignment_submission_status may not exist yet — ignore */
              }
            }

            // Compute course completion (graceful if tables/data are absent).
            try {
              const { result } = await computeCompletionForEnrollment({
                enrollmentId: enrollment.id,
                studentId,
                cohortId: c.id,
              });
              if (active) setCompletion(result);

              // If complete, persist the record so the certificate can render.
              if (result.complete) {
                const { completionRecordId: crId } = await persistCompletion({
                  enrollmentId: enrollment.id,
                  result,
                  student: {
                    full_name: student?.full_name ?? null,
                    cert_number: student?.cert_number ?? null,
                    aircraft_reg: student?.aircraft_reg ?? null,
                  },
                  cohortName: c.name,
                });
                if (active && crId) setCompletionRecordId(crId);
              }
            } catch {
              /* completion tables may not exist yet — ignore */
            }
          }
        }
      }

      if (active) setLoading(false);
    }

    load();
    return () => {
      active = false;
    };
  }, [studentId]);

  const displayName =
    student?.full_name || user?.email?.split("@")[0] || "Pilot";

  async function handleDownloadCertificate() {
    setCertBusy(true);
    setCertError(null);
    const { error } = await openCertificate({
      completionRecordId: completionRecordId ?? undefined,
      enrollmentId: enrollmentId ?? undefined,
    });
    if (error) setCertError(error);
    setCertBusy(false);
  }

  return (
    <div className="min-h-screen bg-slate-50 text-txcfi-ink">
      <AppHeader />
      <main className="mx-auto max-w-5xl px-6 py-10">
        <h1 className="text-2xl font-bold">Welcome, {displayName}</h1>
        <p className="mt-1 text-slate-600">Your 210 Ground School dashboard.</p>

        {/* Enrollment / cohort card */}
        <section className="mt-8 rounded-xl border border-slate-200 bg-white p-6">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
            Your cohort
          </h2>
          {loading ? (
            <p className="mt-2 text-slate-400">Loading…</p>
          ) : cohort ? (
            <div className="mt-2">
              <p className="text-lg font-semibold">{cohort.name}</p>
              <p className="mt-1 text-sm text-slate-500">
                Status: {cohort.status} · {cohort.seats_sold}/{cohort.seat_cap}{" "}
                seats
              </p>
            </div>
          ) : (
            <p className="mt-2 text-slate-500">
              {dbReachable
                ? "No cohort is available yet. Once you're enrolled, your cohort will appear here."
                : "Course data isn't connected yet (database migrations pending). Your cohort and modules will appear here once setup is complete."}
            </p>
          )}
        </section>

        {/* Course completion progress + certificate affordance */}
        {!loading && cohort && enrollmentId && completion && (
          <section className="mt-8 rounded-xl border border-slate-200 bg-white p-6">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
              Course completion
            </h2>
            {completion.complete ? (
              <div className="mt-3">
                <p className="text-lg font-semibold text-emerald-700">
                  Course complete — congratulations!
                </p>
                <p className="mt-1 text-sm text-slate-600">
                  You've met every completion requirement. Your certificate of
                  completion is ready.
                </p>
                <button
                  onClick={handleDownloadCertificate}
                  disabled={certBusy}
                  className="mt-4 rounded-md bg-txcfi-accent px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
                >
                  {certBusy ? "Preparing…" : "Download your certificate"}
                </button>
                {certError && (
                  <p className="mt-2 text-sm text-red-600">{certError}</p>
                )}
              </div>
            ) : (
              <div className="mt-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">
                    {completion.breakdown.completedLessons +
                      completion.breakdown.passedAssignments}{" "}
                    of{" "}
                    {completion.breakdown.totalRequiredLessons +
                      completion.breakdown.totalRequiredAssignments}{" "}
                    requirements met
                  </span>
                  <span className="font-medium text-slate-700">
                    {Math.round(completion.progress * 100)}%
                  </span>
                </div>
                <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-100">
                  <div
                    className="h-full rounded-full bg-txcfi-accent"
                    style={{ width: `${Math.round(completion.progress * 100)}%` }}
                  />
                </div>
                {(completion.breakdown.lessonsOutstanding.length > 0 ||
                  completion.breakdown.assignmentsOutstanding.length > 0) && (
                  <div className="mt-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                      Still outstanding
                    </p>
                    <ul className="mt-2 space-y-1 text-sm text-slate-600">
                      {completion.breakdown.lessonsOutstanding.map((l) => (
                        <li key={l.lessonId}>• {l.title ?? "Lesson"}</li>
                      ))}
                      {completion.breakdown.assignmentsOutstanding.map((a) => (
                        <li key={a.lessonId}>
                          • {a.title ?? "Assignment"}{" "}
                          <span className="text-slate-400">
                            (
                            {a.gradeStatus === "needs_manual_grade"
                              ? "awaiting instructor review"
                              : a.gradeStatus === "failed"
                                ? "not yet passed"
                                : "not submitted"}
                            )
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </section>
        )}

        {/* Module / lesson structure */}
        <section className="mt-8">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
            Course modules
          </h2>
          {loading ? (
            <p className="mt-2 text-slate-400">Loading modules…</p>
          ) : modules.length === 0 ? (
            <div className="mt-2 rounded-xl border border-dashed border-slate-300 bg-white p-6 text-slate-500">
              Modules will appear here once the course content is loaded. The
              course runs across 3 live sessions; workbooks and quizzes unlock as
              the cohort progresses.
            </div>
          ) : (
            <ol className="mt-4 space-y-4">
              {modules.map((m) => (
                <li
                  key={m.id}
                  className="rounded-xl border border-slate-200 bg-white p-5"
                >
                  <div className="flex items-baseline justify-between">
                    <h3 className="font-semibold">
                      {m.block_no ? `Block ${m.block_no} — ` : ""}
                      {m.title}
                    </h3>
                    {m.session_no != null && (
                      <span className="text-xs text-slate-400">
                        Session {m.session_no}
                      </span>
                    )}
                  </div>
                  {m.lessons.length > 0 ? (
                    <ul className="mt-3 space-y-1.5">
                      {m.lessons.map((l) => {
                        const isAssignment =
                          l.type === "workbook" || l.type === "assignment";
                        const done = Boolean(progress[l.id]?.completed_at);
                        const aRow = assignmentStatus[l.id] ?? null;
                        return (
                          <li key={l.id}>
                            <Link
                              to={`/app/lesson/${l.id}`}
                              className="flex items-center justify-between rounded px-2 py-1.5 text-sm hover:bg-slate-50"
                            >
                              <span className="flex items-center gap-2 text-slate-700">
                                {!isAssignment && (
                                  <span
                                    className={
                                      done ? "text-emerald-600" : "text-slate-300"
                                    }
                                    aria-hidden
                                  >
                                    ✓
                                  </span>
                                )}
                                {l.title}
                              </span>
                              <span className="flex items-center gap-2">
                                {isAssignment && <StatusPill row={aRow} />}
                                <span className="rounded bg-slate-100 px-2 py-0.5 text-xs uppercase tracking-wide text-slate-500">
                                  {l.type}
                                </span>
                              </span>
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <p className="mt-2 text-sm text-slate-400">
                      Lessons coming soon.
                    </p>
                  )}
                </li>
              ))}
            </ol>
          )}
        </section>
      </main>
    </div>
  );
}

// Dashboard pill: reflects draft/submitted AND (Day 5) graded states
// (passed / not passed / needs review).
function StatusPill({ row }: { row: AssignmentSubmissionStatusRow | null }) {
  const base =
    "rounded-full px-2 py-0.5 text-[10px] font-medium uppercase";

  if (row?.status === "submitted") {
    switch (row.grade_status) {
      case "passed":
        return (
          <span className={`${base} bg-emerald-100 text-emerald-800`}>
            Passed
          </span>
        );
      case "failed":
        return (
          <span className={`${base} bg-red-100 text-red-800`}>Not passed</span>
        );
      case "needs_manual_grade":
        return (
          <span className={`${base} bg-sky-100 text-sky-800`}>Needs review</span>
        );
      default:
        return (
          <span className={`${base} bg-emerald-100 text-emerald-800`}>
            Submitted
          </span>
        );
    }
  }
  if (row?.status === "draft") {
    return <span className={`${base} bg-amber-100 text-amber-800`}>Draft</span>;
  }
  return (
    <span className={`${base} bg-slate-100 text-slate-500`}>Not started</span>
  );
}
